// Typed custom metrics: a user-assigned type (+ optional subtype) on a
// CompanyCustomMetric that opts its values into the existing high-level KPIs.
// Generalizes the single-purpose `includeInAdSpendTotal` boolean (whose
// behavior is now `type: 'ad_spend'` — the add-columns migration backfills it).
//
// The values are summed in on the FRONTEND only (they never reach Cube or the
// pipeline), in two distinct operations:
//
//   1. COLUMN FOLD — each typed metric's bucketed series is added onto the
//      aggregate measure COLUMNS of the merged KPI dataset (current cells and
//      their `compared.*` YoY twins), BEFORE the row builders run. Every
//      consumer — builtin tables, custom-report groups, computed metrics, calc
//      formulas, WoW rows, charts, exports — then sees consistent values, and
//      the per-row YoY change derives correctly for free. Cube-side RATIO
//      measures (MER family, aMER, CM%) cannot see the fold, so the affected
//      ratio cells are re-derived here with formulas mirroring the cube SQL
//      (see TYPED_RATIO_RECOMPUTES); untouched buckets keep their cube values
//      byte-for-byte.
//   2. ROW INSERTION — the metric also renders as its own row inside the table
//      that owns its primary target (mirroring the Ad Spend behavior), so the
//      folded total's composition stays visible. That is UI row shaping and
//      lives in apps/ui-rtk .../KPI/utils/custom-metric-rows.ts; this module
//      only declares each type's insertion anchor keys.
//
// Intentional non-targets (product decisions, 2026-08-03):
//   - aMER-family NUMERATORS (new-customer revenue) never receive custom
//     revenue — custom metrics have no new/repeat split. Custom ad spend DOES
//     grow their denominators.
//   - `total_direct_spend` (the DTC MER denominator) never receives custom ad
//     spend — "direct" is a curated channel sum and custom spend carries no
//     channel identity.
//   - Gross-profit / contribution-margin dollars never receive custom revenue
//     or custom ad spend (dbt computes them channel-aware); only
//     `variable_cost` metrics subtract from Total Business CM.
//   - `fixed_cost` is tag-only: no fold target exists until a net-profit
//     metric does. By definition it does not belong in contribution margin.
//   - Wholesale-tagged revenue folds into the 3rd Party Retail Revenue rollup
//     (`wholesale_total_revenue`, the sum of every retailer sell-through row)
//     plus Total Business Revenue, and renders beside that total — never into
//     a retailer-specific row such as `wholesale_rei_regular_revenue`.
//
// The fold is frontend-only by design: the CLI / API / Slack reports serve
// raw cube values and will not include typed custom metrics.

import { isAdditiveFormat } from './custom-metric-buckets';
import { initiativeCategoryMetricKey, type InitiativeCategoryValue } from './initiative-metrics';
import { toFiniteNumber } from './numeric';

export const CUSTOM_METRIC_TYPES = ['revenue', 'ad_spend', 'variable_cost', 'fixed_cost'] as const;
export type CustomMetricType = (typeof CUSTOM_METRIC_TYPES)[number];

export const REVENUE_CHANNEL_SUBTYPES = [
  'dtc_ecomm',
  'subscription',
  'dtc_retail',
  'third_party_ecomm',
  'third_party_retail',
  'amazon',
  'wholesale',
  'other',
] as const;
export type RevenueChannelSubtype = (typeof REVENUE_CHANNEL_SUBTYPES)[number];

export const AD_SPEND_SUBTYPES = ['demand_generation', 'demand_capture'] as const;
export type AdSpendSubtype = (typeof AD_SPEND_SUBTYPES)[number];

export const CUSTOM_METRIC_TYPE_LABELS: Record<CustomMetricType, string> = {
  revenue: 'Revenue',
  ad_spend: 'Ad Spend',
  variable_cost: 'Variable Cost',
  fixed_cost: 'Fixed Cost',
};

export const REVENUE_CHANNEL_SUBTYPE_LABELS: Record<RevenueChannelSubtype, string> = {
  dtc_ecomm: 'DTC E-Commerce',
  subscription: 'Subscription',
  dtc_retail: 'DTC Retail',
  third_party_ecomm: 'Third Party E-Commerce',
  third_party_retail: 'Third Party Retail',
  amazon: 'Amazon',
  wholesale: 'Wholesale',
  other: 'Other',
};

export const AD_SPEND_SUBTYPE_LABELS: Record<AdSpendSubtype, string> = {
  demand_generation: 'Demand Generation',
  demand_capture: 'Demand Capture',
};

export function isCustomMetricType(value: unknown): value is CustomMetricType {
  return typeof value === 'string' && (CUSTOM_METRIC_TYPES as readonly string[]).includes(value);
}

/** Valid subtype values for a type; null = the type takes no subtype. */
export function subtypesForType(type: CustomMetricType): readonly string[] | null {
  if (type === 'revenue') return REVENUE_CHANNEL_SUBTYPES;
  if (type === 'ad_spend') return AD_SPEND_SUBTYPES;
  return null;
}

/** True when (type, subtype) is a storable pair; subtype is always optional. */
export function isValidTypeSubtype(type: CustomMetricType, subtype: string | null | undefined): boolean {
  if (subtype === null || subtype === undefined) return true;
  const allowed = subtypesForType(type);
  return allowed !== null && allowed.includes(subtype);
}

/** "Revenue — DTC E-Commerce" style label for list rows and badges. */
export function typedCustomMetricLabel(type: CustomMetricType, subtype: string | null | undefined): string {
  const typeLabel = CUSTOM_METRIC_TYPE_LABELS[type];
  if (!subtype) return typeLabel;
  let subtypeLabel: string | undefined;
  if (type === 'revenue') {
    subtypeLabel = REVENUE_CHANNEL_SUBTYPE_LABELS[subtype as RevenueChannelSubtype];
  } else if (type === 'ad_spend') {
    subtypeLabel = AD_SPEND_SUBTYPE_LABELS[subtype as AdSpendSubtype];
  }
  return subtypeLabel ? `${typeLabel} — ${subtypeLabel}` : typeLabel;
}

// --- Fold targets -----------------------------------------------------------

const K = {
  totalAdSpend: 'metrics.total_ad_spend',
  newCustomerRevenue: 'metrics.shopify_total_new_customer_revenue',
  shopifyTotal: 'metrics.shopify_total_customer_revenue',
  ecommTotal: 'metrics.shopify_ecomm_total_customer_revenue',
  subscriptionTotal: 'metrics.shopify_subscription_total_customer_revenue',
  dtcRetailTotal: 'metrics.shopify_dtc_retail_total_customer_revenue',
  thirdPartyEcommTotal: 'metrics.shopify_third_party_ecomm_total_customer_revenue',
  amazonTotal: 'metrics.amazon_total_revenue',
  wholesaleTotal: 'metrics.wholesale_total_revenue',
  tbrShopify: 'metrics.total_business_revenue_shopify',
  tbrGa: 'metrics.total_business_revenue_ga',
  totalBusinessCm: 'metrics.total_business_contribution_margin',
  ecommCm: 'metrics.total_contribution_margin',
  tiktokSpend: 'metrics.tiktok_ads_spend',
  instagramSpend: 'metrics.instagram_ads_spend',
  facebookSpend: 'metrics.facebook_ads_spend',
  googleSpend: 'metrics.google_ads_total_spend',
  pinterestSpend: 'metrics.pinterest_ads_spend',
} as const;

// Both Total Business Revenue variants are patched; only one renders (chosen
// by isShopifyConnected in the dynamic group), so revenue always lands in
// whichever TBR row the company sees.
const TBR_COLUMNS = [K.tbrShopify, K.tbrGa];

export interface TypedFoldTargets {
  /** `metrics.*` columns of the main KPI dataset the values add into. */
  mainColumns: readonly string[];
  /** `initiative_category.*` columns of the pivoted adset dataset (DG/DC). */
  initiativeColumns: readonly string[];
  /** +1 to add (revenue / ad spend), −1 to subtract (variable costs). */
  sign: 1 | -1;
  /**
   * Row-insertion anchors of the metric's PRIMARY table: it renders as a row
   * immediately before the first of these keys. A list, not a single key,
   * only because Total Business Revenue is a prefs-dependent key pair.
   */
  insertAnchors: readonly string[];
  /**
   * Fallback anchors used only when no rendered table contains a primary
   * anchor (e.g. the Subscription / Third Party Ecomm revenue tables are not
   * on the builtin dashboard, and the Wholesale table is connector-gated) —
   * the row then lands beside the Total Business Revenue row instead, so a
   * folded total never grows invisibly. Also identifies the rollup table the
   * cross-table placement must NOT prefer when the primary anchor appears in
   * several tables (metrics.amazon_total_revenue is a row of both the Amazon
   * Revenue and Total Business Revenue tables).
   */
  fallbackInsertAnchors: readonly string[];
}

const AD_SPEND_CATEGORY_BY_SUBTYPE: Record<AdSpendSubtype, InitiativeCategoryValue> = {
  demand_generation: 'DEMAND_GENERATION',
  demand_capture: 'DEMAND_CAPTURE',
};

interface RevenueSubtypeTargets {
  mainColumns: string[];
  insertAnchors: readonly string[];
  fallbackInsertAnchors: readonly string[];
}

const REVENUE_TARGETS_BY_SUBTYPE: Record<RevenueChannelSubtype, RevenueSubtypeTargets> = {
  dtc_ecomm: {
    mainColumns: [K.ecommTotal, K.shopifyTotal, ...TBR_COLUMNS],
    insertAnchors: [K.ecommTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  subscription: {
    mainColumns: [K.subscriptionTotal, K.shopifyTotal, ...TBR_COLUMNS],
    // The Subscription revenue table is not on the builtin dashboard — the
    // fallback keeps the row visible beside the Total Business Revenue row.
    insertAnchors: [K.subscriptionTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  dtc_retail: {
    mainColumns: [K.dtcRetailTotal, K.shopifyTotal, ...TBR_COLUMNS],
    insertAnchors: [K.dtcRetailTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  third_party_ecomm: {
    mainColumns: [K.thirdPartyEcommTotal, K.shopifyTotal, ...TBR_COLUMNS],
    // Same as subscription: no builtin Third Party Ecomm table.
    insertAnchors: [K.thirdPartyEcommTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  // No revenue measure exists for Third Party Retail — totals only.
  third_party_retail: { mainColumns: [...TBR_COLUMNS], insertAnchors: TBR_COLUMNS, fallbackInsertAnchors: [] },
  amazon: {
    mainColumns: [K.amazonTotal, ...TBR_COLUMNS],
    insertAnchors: [K.amazonTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  // Folds into the 3rd Party Retail Revenue rollup (never a retailer-specific
  // row) and renders beside it; the wholesale table is connector-gated, so the
  // fallback matters.
  wholesale: {
    mainColumns: [K.wholesaleTotal, ...TBR_COLUMNS],
    insertAnchors: [K.wholesaleTotal],
    fallbackInsertAnchors: TBR_COLUMNS,
  },
  other: { mainColumns: [...TBR_COLUMNS], insertAnchors: TBR_COLUMNS, fallbackInsertAnchors: [] },
};

/**
 * Fold targets for a (type, subtype) pair. An unknown subtype (or none) falls
 * back to the type's broadest scope: revenue → Total Business Revenue only,
 * ad spend → Total Ad Spend only. `fixed_cost` returns empty targets (tag-only).
 */
export function foldTargetsFor(type: CustomMetricType, subtype: string | null | undefined): TypedFoldTargets {
  if (type === 'ad_spend') {
    const category = subtype ? AD_SPEND_CATEGORY_BY_SUBTYPE[subtype as AdSpendSubtype] : undefined;
    return {
      mainColumns: [K.totalAdSpend],
      initiativeColumns: category ? [initiativeCategoryMetricKey(category, 'spend')] : [],
      sign: 1,
      insertAnchors: [K.totalAdSpend],
      fallbackInsertAnchors: [],
    };
  }
  if (type === 'revenue') {
    const targets = (subtype && REVENUE_TARGETS_BY_SUBTYPE[subtype as RevenueChannelSubtype]) || {
      mainColumns: [...TBR_COLUMNS],
      insertAnchors: TBR_COLUMNS,
      fallbackInsertAnchors: [] as readonly string[],
    };
    return {
      mainColumns: targets.mainColumns,
      initiativeColumns: [],
      sign: 1,
      insertAnchors: targets.insertAnchors,
      fallbackInsertAnchors: targets.fallbackInsertAnchors,
    };
  }
  if (type === 'variable_cost') {
    return {
      mainColumns: [K.totalBusinessCm],
      initiativeColumns: [],
      sign: -1,
      insertAnchors: [K.totalBusinessCm],
      fallbackInsertAnchors: [],
    };
  }
  // fixed_cost: stored for future use (no net-profit metric exists yet).
  return { mainColumns: [], initiativeColumns: [], sign: 1, insertAnchors: [], fallbackInsertAnchors: [] };
}

export interface TypedCustomMetricLike {
  type?: string | null;
  subtype?: string | null;
  format: string;
}

/**
 * Fold targets for a stored metric, or null when it does not participate:
 * untyped, or a non-additive format (percent/decimal values must not sum into
 * currency aggregates — same guard the Ad Spend fold has always had).
 */
export function foldTargetsForMetric(metric: TypedCustomMetricLike): TypedFoldTargets | null {
  if (!isCustomMetricType(metric.type)) return null;
  if (!isAdditiveFormat(metric.format as Parameters<typeof isAdditiveFormat>[0])) return null;
  return foldTargetsFor(metric.type, metric.subtype);
}

// --- Column fold ------------------------------------------------------------

/** Per-column, per-bucket-index deltas for one dataset (current + YoY side). */
export interface TypedColumnDeltas {
  current: Map<string, number[]>;
  previous: Map<string, number[]>;
}

export interface TypedSeriesFold {
  /** Bucketed values index-aligned with the KPI column buckets. */
  series: { current: (number | null)[]; previous: (number | null)[] };
  columns: readonly string[];
  sign: 1 | -1;
}

/**
 * Accumulates the typed metrics' bucketed series into per-column delta arrays.
 * Null series entries (no value entered for that bucket) contribute nothing.
 * Columns whose deltas are all zero are dropped, so consumers can treat map
 * membership as "this column was actually touched".
 */
export function buildTypedColumnDeltas(folds: TypedSeriesFold[], bucketCount: number): TypedColumnDeltas {
  const current = new Map<string, number[]>();
  const previous = new Map<string, number[]>();
  const accumulate = (map: Map<string, number[]>, column: string, index: number, value: number | null, sign: 1 | -1) => {
    // Skipping 0 alongside null is intentional, not an oversight: a zero
    // delta cannot change any cell, and dropping it keeps a metric whose
    // entered values are all 0 from marking its columns as "touched".
    if (value === null || value === 0) return;
    let arr = map.get(column);
    if (!arr) {
      arr = new Array<number>(bucketCount).fill(0);
      map.set(column, arr);
    }
    arr[index] += sign * value;
  };
  for (const fold of folds) {
    for (const column of fold.columns) {
      for (let index = 0; index < bucketCount; index += 1) {
        accumulate(current, column, index, fold.series.current[index] ?? null, fold.sign);
        accumulate(previous, column, index, fold.series.previous[index] ?? null, fold.sign);
      }
    }
  }
  // Deleting the currently-yielded entry while iterating a Map is spec-safe
  // (Map iterators tolerate deletion of visited keys) — no snapshot needed.
  const dropAllZero = (map: Map<string, number[]>) => {
    for (const [column, arr] of map) {
      if (arr.every((value) => value === 0)) map.delete(column);
    }
  };
  dropAllZero(current);
  dropAllZero(previous);
  return { current, previous };
}

export function hasAnyTypedDelta(deltas: TypedColumnDeltas): boolean {
  return deltas.current.size > 0 || deltas.previous.size > 0;
}

// --- Ratio recomputation ----------------------------------------------------

/** Mirrors the cube's CAST(… AS DECIMAL(10, 2)) on the MER-family measures. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface RatioRecompute {
  key: string;
  /** Columns the formula reads; a cell recomputes only when a patched input's delta ≠ 0 for that bucket. */
  inputs: readonly string[];
  compute: (get: (key: string) => number) => number | null;
}

const DIRECT_SPEND_COLUMNS = [K.tiktokSpend, K.instagramSpend, K.facebookSpend, K.pinterestSpend, K.googleSpend] as const;

// `total_direct_spend` (social + Google, excl. Amazon) is not fetched by the
// KPI query, so the DTC MER denominators re-derive it from the per-channel
// spend columns — which never receive custom values, matching the cube.
function directSpend(get: (key: string) => number): number {
  return DIRECT_SPEND_COLUMNS.reduce((sum, column) => sum + get(column), 0);
}

/**
 * The displayed cube-side ratio measures whose components can receive typed
 * custom values. Formulas mirror cube/model/company_data_daily.js exactly
 * (zero-denominator → 0, CAST rounding), so an untouched bucket recomputed
 * here would produce the same cell the cube did — but we still only recompute
 * buckets that actually received a delta, keeping every other cell pristine.
 *
 * Deliberately absent (their inputs are never patched): `dtc_ecomm_amer` and
 * `total_dtc_amer` (new-customer numerators over direct spend).
 */
export const TYPED_RATIO_RECOMPUTES: readonly RatioRecompute[] = [
  {
    // aMER = new-customer revenue / total ad spend (cdd.js:2204-2213)
    key: 'metrics.amer',
    inputs: [K.totalAdSpend, K.newCustomerRevenue],
    compute: (get) => {
      const spend = get(K.totalAdSpend);
      return spend > 0 ? round2(get(K.newCustomerRevenue) / spend) : 0;
    },
  },
  {
    // total ad spend / new-customer revenue × 100 (cdd.js:2193-2203)
    key: 'metrics.spend_percentage_of_new_customer_revenue',
    inputs: [K.totalAdSpend, K.newCustomerRevenue],
    compute: (get) => {
      const revenue = get(K.newCustomerRevenue);
      return revenue > 0 ? (get(K.totalAdSpend) / revenue) * 100 : 0;
    },
  },
  {
    // Total Business Revenue (Shopify variant) / total ad spend (cdd.js:2273-2282).
    // The cube defines this measure via total_business_revenue_shopify for
    // EVERY company — including GA-connected ones, where Shopify revenue is 0
    // and the measure degrades to (Amazon revenue / spend). tbrGa is therefore
    // deliberately NOT an input here: mirroring the cube exactly is what keeps
    // delta-touched buckets consistent with untouched ones.
    key: 'metrics.total_business_mer',
    inputs: [K.totalAdSpend, K.tbrShopify],
    compute: (get) => {
      const spend = get(K.totalAdSpend);
      return spend > 0 ? round2(get(K.tbrShopify) / spend) : 0;
    },
  },
  {
    // (ecomm total + Amazon total) / total ad spend (cdd.js:2283-2292)
    key: 'metrics.dtc_ecomm_plus_amazon_mer',
    inputs: [K.totalAdSpend, K.ecommTotal, K.amazonTotal],
    compute: (get) => {
      const spend = get(K.totalAdSpend);
      return spend > 0 ? round2((get(K.ecommTotal) + get(K.amazonTotal)) / spend) : 0;
    },
  },
  {
    // ecomm total / direct spend (cdd.js:2233-2242)
    key: 'metrics.dtc_ecomm_mer',
    inputs: [K.ecommTotal, ...DIRECT_SPEND_COLUMNS],
    compute: (get) => {
      const spend = directSpend(get);
      return spend > 0 ? round2(get(K.ecommTotal) / spend) : 0;
    },
  },
  {
    // Shopify total customer revenue / direct spend (cdd.js:2253-2262)
    key: 'metrics.total_dtc_mer',
    inputs: [K.shopifyTotal, ...DIRECT_SPEND_COLUMNS],
    compute: (get) => {
      const spend = directSpend(get);
      return spend > 0 ? round2(get(K.shopifyTotal) / spend) : 0;
    },
  },
  {
    // 100 × ecomm CM / (ecomm + subscription revenue); NULLIF(denom) → null (cdd.js:1567-1576)
    key: 'metrics.contribution_margin_percent',
    inputs: [K.ecommCm, K.ecommTotal, K.subscriptionTotal],
    compute: (get) => {
      const denominator = get(K.ecommTotal) + get(K.subscriptionTotal);
      return denominator !== 0 ? (get(K.ecommCm) / denominator) * 100 : null;
    },
  },
];

/**
 * Applies the typed deltas onto a merged compareDateRange dataset: each row's
 * patched columns gain their bucket's delta on the current cell and the
 * `compared.*` twin, then the affected ratio cells are re-derived per side.
 * Rows are matched to bucket indexes by date (never positionally). Returns
 * the input array identity when there is nothing to fold. Cube cells may be
 * raw strings — all arithmetic coerces via toFiniteNumber, and an absent
 * `compared.*` twin reads as 0 (a custom prior-year value still yields a YoY).
 */
export function applyTypedColumnDeltas(options: {
  rows: Array<Record<string, unknown>> | null;
  dateKey: string;
  buckets: ReadonlyArray<{ start: string }>;
  deltas: TypedColumnDeltas;
  ratioRecomputes?: readonly RatioRecompute[];
  /**
   * For SIBLING datasets that are not guaranteed bucket-complete (the adset
   * initiative dataset — the buckets come from the MAIN dataset): create a
   * date-keyed row for any bucket that carries a delta but has no row, so a
   * DG/DC-tagged custom spend still shows for weeks where the adset cube
   * returned nothing (all campaigns paused, or no platform ads at all).
   * Synthetic rows hold only the delta columns; row builders read absent
   * cells as 0. Never needed for the main dataset (its rows define the
   * buckets), and no-op while `rows` is null (query not yet resolved — the
   * page's loading gates cover that state).
   */
  synthesizeMissingBucketRows?: boolean;
}): Array<Record<string, unknown>> | null {
  const { rows, dateKey, buckets, deltas, ratioRecomputes = [], synthesizeMissingBucketRows = false } = options;
  if (!rows || !hasAnyTypedDelta(deltas)) return rows;
  if (rows.length === 0 && !synthesizeMissingBucketRows) return rows;

  const indexByBucketStart = new Map(buckets.map((bucket, index) => [bucket.start, index]));
  const relevantRatios = ratioRecomputes.filter((ratio) =>
    ratio.inputs.some((input) => deltas.current.has(input) || deltas.previous.has(input)),
  );

  const matchedBucketIndexes = new Set<number>();
  const patchedRows = rows.map((row) => {
    const bucketIndex = indexByBucketStart.get(String(row[dateKey] ?? '').slice(0, 10));
    if (bucketIndex === undefined) return row;
    matchedBucketIndexes.add(bucketIndex);

    let patched: Record<string, unknown> | null = null;
    const currentTouched = new Set<string>();
    const previousTouched = new Set<string>();

    for (const [column, columnDeltas] of deltas.current) {
      const delta = columnDeltas[bucketIndex];
      if (!delta) continue;
      patched ??= { ...row };
      patched[column] = toFiniteNumber(row[column]) + delta;
      currentTouched.add(column);
    }
    for (const [column, columnDeltas] of deltas.previous) {
      const delta = columnDeltas[bucketIndex];
      if (!delta) continue;
      patched ??= { ...row };
      patched[`compared.${column}`] = toFiniteNumber(row[`compared.${column}`]) + delta;
      previousTouched.add(column);
    }
    if (!patched) return row;
    const patchedRow = patched;

    for (const ratio of relevantRatios) {
      if (ratio.inputs.some((input) => currentTouched.has(input))) {
        patchedRow[ratio.key] = ratio.compute((key) => toFiniteNumber(patchedRow[key]));
      }
      if (ratio.inputs.some((input) => previousTouched.has(input))) {
        patchedRow[`compared.${ratio.key}`] = ratio.compute((key) => toFiniteNumber(patchedRow[`compared.${key}`]));
      }
    }
    return patchedRow;
  });

  if (!synthesizeMissingBucketRows) return patchedRows;

  // Delta-carrying buckets with no dataset row get a minimal date-keyed row
  // (delta columns only — no ratio recomputation: every other input is absent,
  // i.e. genuinely zero). Downstream row builders re-sort by date themselves.
  const syntheticRows: Array<Record<string, unknown>> = [];
  buckets.forEach((bucket, bucketIndex) => {
    if (matchedBucketIndexes.has(bucketIndex)) return;
    let synthetic: Record<string, unknown> | null = null;
    for (const [column, columnDeltas] of deltas.current) {
      const delta = columnDeltas[bucketIndex];
      if (!delta) continue;
      synthetic ??= { [dateKey]: bucket.start };
      synthetic[column] = delta;
    }
    for (const [column, columnDeltas] of deltas.previous) {
      const delta = columnDeltas[bucketIndex];
      if (!delta) continue;
      synthetic ??= { [dateKey]: bucket.start };
      synthetic[`compared.${column}`] = delta;
    }
    if (synthetic) syntheticRows.push(synthetic);
  });
  return syntheticRows.length > 0 ? [...patchedRows, ...syntheticRows] : patchedRows;
}
